import {
  Divider,
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@heroui/react';
import {
  ArrowRight,
  HelpCircle,
  Lock,
  NavArrowDown,
  ServerConnection,
  Settings
} from 'iconoir-react';
import { FC, useState } from 'react';

import { BrandMenuItem } from './components/BrandMenuItem';
import { HelpSubmenu, type Eula } from './components/HelpSubmenu';
import { ProfileSubmenu } from './components/ProfileSubmenu';

import featureFlagConstants from '~/common/helpers/featureFlagConstants';
import i18n from '~/common/helpers/i18n';
import useFeatureFlag from '~/common/hooks/useFeatureFlag';
import { usePlatformLogo } from '~/common/hooks/usePlatformLogo';

interface BrandMenuProps {
  goToAdmin: () => void;
  goToSettings: () => void;
  goToConnectivity: () => void;
  showConnectivity: boolean;
  unreadCount?: number;
  onToggleChat: () => void;
  eula?: Eula | null;
  openEula: () => void;
  openSupportAccess: () => void;
  enabledSupportAccess?: boolean;
  userName?: string;
  isMainRealm: boolean;
  setModalProfile: (open: boolean) => void;
  setModalNewPassword: (open: boolean) => void;
  setModalTwoFactor: (open: boolean) => void;
  onSignOut: () => void;
}

type SubmenuKey = 'help' | 'profile' | null;

export const BrandMenu: FC<BrandMenuProps> = ({
  goToAdmin,
  goToSettings,
  goToConnectivity,
  showConnectivity,
  unreadCount,
  onToggleChat,
  eula,
  openEula,
  openSupportAccess,
  enabledSupportAccess,
  userName,
  isMainRealm,
  setModalProfile,
  setModalNewPassword,
  setModalTwoFactor,
  onSignOut
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [openSubmenu, setOpenSubmenu] = useState<SubmenuKey>(null);
  const { buildLogo } = usePlatformLogo();
  const { CONNECTIVITY_ZTNA_MVP_ENABLED_FRONT } = featureFlagConstants;
  const { treatments } = useFeatureFlag([CONNECTIVITY_ZTNA_MVP_ENABLED_FRONT]);

  const showConnectivityItem =
    showConnectivity &&
    treatments[CONNECTIVITY_ZTNA_MVP_ENABLED_FRONT]?.treatment === 'on';
  const hasNotification =
    (unreadCount ?? 0) > 0 || Boolean(eula && !eula.acceptanceDate);

  const close = () => {
    setIsOpen(false);
    setOpenSubmenu(null);
  };

  // Actions run one tick after the popover unmounts: mounting an overlay
  // (beehive SideSheet/Radix) while the react-aria popover is alive locks
  // both libraries in an infinite focus/aria management loop.
  const runAndClose = (action: () => void) => () => {
    close();
    setTimeout(action, 0);
  };

  return (
    <Popover
      isOpen={isOpen}
      onOpenChange={open => {
        setIsOpen(open);
        if (!open) setOpenSubmenu(null);
      }}
      placement='bottom-start'
      triggerScaleOnOpen={false}
      disableAnimation
    >
      <PopoverTrigger>
        <button
          type='button'
          data-testid='header-brand-menu'
          aria-label='Digibee'
          className='flex items-center gap-1 outline-none'
        >
          <img
            src={buildLogo}
            alt='Digibee Logo'
            className='h-[39px] w-[34px]'
          />
          <NavArrowDown className='icon-small text-on-surface' />
        </button>
      </PopoverTrigger>
      <PopoverContent className='min-w-[182px] items-stretch overflow-visible p-2'>
        <div className='flex flex-col gap-1'>
          <BrandMenuItem
            data-testid='header-administration-button'
            icon={<Lock className='icon-smallest shrink-0' />}
            label={i18n.t('scenes.beta.labels.administration')}
            onPress={runAndClose(goToAdmin)}
            onHover={() => setOpenSubmenu(null)}
          />
          <BrandMenuItem
            data-testid='header-settings-button'
            icon={<Settings className='icon-smallest shrink-0' />}
            label={i18n.t('scenes.beta.labels.setting_plural')}
            onPress={runAndClose(goToSettings)}
            onHover={() => setOpenSubmenu(null)}
          />
          <div className='relative' onMouseEnter={() => setOpenSubmenu('help')}>
            <BrandMenuItem
              data-testid='header-help-menu'
              icon={<HelpCircle className='icon-smallest shrink-0' />}
              label={i18n.t('noun.help')}
              isActive={openSubmenu === 'help'}
              onPress={() => setOpenSubmenu('help')}
              endContent={
                <span className='flex shrink-0 items-center gap-1'>
                  {hasNotification ? (
                    <span
                      data-testid='header-help-notification'
                      className='size-2 rounded-full bg-negative'
                    />
                  ) : null}
                  <ArrowRight className='icon-smallest shrink-0' />
                </span>
              }
            />
            {openSubmenu === 'help' ? (
              <HelpSubmenu
                unreadCount={unreadCount}
                eula={eula}
                enabledSupportAccess={enabledSupportAccess}
                onChat={runAndClose(onToggleChat)}
                onSupportAccess={runAndClose(openSupportAccess)}
                onLicenseAgreement={runAndClose(openEula)}
                onLinkPress={close}
              />
            ) : null}
          </div>
          {showConnectivityItem ? (
            <BrandMenuItem
              data-testid='header-connectivity-button'
              icon={<ServerConnection className='icon-smallest shrink-0' />}
              label={i18n.t('label.connectivity')}
              onPress={runAndClose(goToConnectivity)}
              onHover={() => setOpenSubmenu(null)}
            />
          ) : null}
        </div>
        <Divider className='my-2 bg-outline' />
        <div
          className='relative'
          onMouseEnter={() => setOpenSubmenu('profile')}
        >
          <button
            type='button'
            data-testid='profile-show-menu-button'
            onClick={() => setOpenSubmenu('profile')}
            className='flex w-full items-center gap-2 rounded-md p-2 text-left outline-none transition-colors hover:bg-surface-container-low focus-visible:bg-surface-container-low'
          >
            <span className='flex size-7 shrink-0 items-center justify-center rounded-full bg-surface-container text-body-regular-md text-on-surface'>
              {userName?.charAt(0)?.toUpperCase() ?? ''}
            </span>
            <span className='fs-mask flex-1 whitespace-nowrap text-body-regular-sm text-on-surface-variant'>
              {userName}
            </span>
            <ArrowRight className='icon-smallest shrink-0 text-on-surface-variant' />
          </button>
          {openSubmenu === 'profile' ? (
            <ProfileSubmenu
              isMainRealm={isMainRealm}
              onEditProfile={runAndClose(() => setModalProfile(true))}
              onChangePassword={runAndClose(() => setModalNewPassword(true))}
              onTwoFactor={runAndClose(() => setModalTwoFactor(true))}
              onSignOut={runAndClose(onSignOut)}
            />
          ) : null}
        </div>
      </PopoverContent>
    </Popover>
  );
};
